import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  alpha, Alert, Box, Button, Chip, CircularProgress, LinearProgress, Grid, Menu, MenuItem, Skeleton, Stack, TextField, Typography
} from '@mui/material';
import { apiGet } from 'api/service';
import { downloadAdReport } from 'api/adAudit';
import { downloadReconExport } from 'api/recon';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';

const GREEN = '#22c55e';
const SEV = ['critical', 'high', 'medium', 'low', 'info'];
const SEV_COLOR = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#22c55e', info: '#64748b' };
const TYPE_LABEL = { subdomain: 'Domains', ip: 'IP addresses', url: 'Websites', mobile: 'Mobile apps', cidr: 'Network ranges', other: 'Other' };
const STATUS_COLOR = { completed: GREEN, running: '#0ea5e9', pending: '#f59e0b', failed: '#ef4444', cancelled: '#94a3b8' };

const rows = (d) => (Array.isArray(d) ? d : d?.results || []);
const when = (v) => (v ? new Date(v).toLocaleDateString() : '—');

function Card({ title, action, children, sx }) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 4, p: 2.5, bgcolor: 'background.paper', height: '100%', ...sx }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

export default function Target360Page() {
  const navigate = useNavigate();
  const { targets, currentTargetId, setCurrentTargetId, loading } = useFuzzScanTarget();
  const selected = useMemo(
    () => targets.find((t) => String(t.id) === String(currentTargetId)) || null, [targets, currentTargetId]
  );
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lang, setLang] = useState('en');
  const [menuEl, setMenuEl] = useState(null);

  const load = useCallback(async () => {
    if (!currentTargetId) { setData(null); return; }
    setBusy(true);
    const q = `user_target=${currentTargetId}`;
    const [assets, scans, summary, top, ad, periods, risk] = await Promise.allSettled([
      apiGet(`/api/assets/?${q}&page_size=500`),
      apiGet(`/api/scans/?${q}&page_size=8`),
      apiGet(`/api/findings/status-summary/?${q}`),
      apiGet(`/api/findings/?${q}&status=open_only&page_size=40`),
      apiGet(`/api/ad-audit/list/?${q}`),
      apiGet(`/api/vuln-benchmark/scans/?${q}`),
      apiGet(`/api/target-risk/?${q}`)
    ]);
    const ok = (r, fb) => (r.status === 'fulfilled' ? r.value : fb);
    setData({
      assets: rows(ok(assets, [])),
      scans: rows(ok(scans, [])),
      summary: ok(summary, null),
      top: rows(ok(top, [])).sort((x, y) => SEV.indexOf(x.severity) - SEV.indexOf(y.severity)).slice(0, 5),
      risk: ok(risk, null),
      audits: ok(ad, { audits: [] }).audits || [],
      periods: ok(periods, { scans: [] }).scans || []
    });
    setBusy(false);
  }, [currentTargetId]);

  useEffect(() => { load(); }, [load]);

  const sev = data?.summary?.by_severity || { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
  const risk = data?.risk;
  const GRADE_COLOR = { A: '#22c55e', B: '#84cc16', C: '#eab308', D: '#f97316', F: '#ef4444' };
  const gcolor = GRADE_COLOR[risk?.grade] || '#64748b';
  const trend = risk?.trend || [];
  const maxOpen = Math.max(1, ...trend.map((t) => t.open));
  const assetCounts = useMemo(() => {
    const out = {};
    (data?.assets || []).forEach((a) => { out[a.type] = (out[a.type] || 0) + 1; });
    return out;
  }, [data]);
  const latestAudit = data?.audits?.[0];
  const dl = (fn) => fn().catch((e) => setError(e?.message || 'Download failed.'));

  const runs = [['Recon', '/recon'], ['Web scan', '/web-scans'], ['Network scan', '/network-scans'], ['Mobile analysis', '/mobile-scans']];
  const sevCards = SEV.slice(0, 4);

  return (
    <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>Target overview</Typography>
          <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography component="h1" sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800, lineHeight: 1.2 }}>
              {selected?.name || selected?.target?.name || selected?.target?.value || 'Target'}
            </Typography>
            {selected?.target?.value && (
              <Box sx={{ fontFamily: 'var(--fuzzscan-ui-font-mono)', fontSize: 13, px: 1.5, py: 0.6, borderRadius: 2, bgcolor: 'action.hover' }}>{selected.target.value}</Box>
            )}
          </Stack>
        </Box>
        <Stack direction="row" gap={1.2} flexWrap="wrap">
          <Button variant="outlined" onClick={(e) => setMenuEl(e.currentTarget)} sx={{ minHeight: 44, borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Switch target</Button>
          <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
            {targets.map((t) => (
              <MenuItem key={t.id} selected={String(t.id) === String(currentTargetId)}
                onClick={() => { setCurrentTargetId(String(t.id)); setMenuEl(null); }}>
                {t.name || t.target?.name || t.target?.value || 'Mobile app'}
              </MenuItem>
            ))}
          </Menu>
          <Button variant="outlined" onClick={() => navigate('/targets')} sx={{ minHeight: 44, borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Manage</Button>
          <Button variant="contained" onClick={() => navigate(selected?.target?.type === 'mobile' ? '/mobile-scans' : '/web-scans')}
            sx={{ minHeight: 44, bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>Start a scan</Button>
        </Stack>
      </Stack>

      {!loading && !selected && <Alert severity="info">No target yet. Create one on the Targets page.</Alert>}

      {selected && (
        <Stack spacing={3}>
          {/* score + severity */}
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 5, lg: 4 }}>
              <Card title="Risk score">
                <Stack direction="row" alignItems="center" gap={3} flexWrap="wrap">
                  <Box sx={{ position: 'relative', width: 140, height: 140 }}>
                    <CircularProgress variant="determinate" value={100} size={140} thickness={4.5} sx={{ position: 'absolute', color: 'action.hover' }} />
                    <CircularProgress variant="determinate" value={busy ? 0 : (risk?.score ?? 0)} size={140} thickness={4.5} sx={{ position: 'absolute', color: gcolor }} />
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: 38, fontWeight: 800, fontFamily: 'var(--fuzzscan-ui-font-mono)', lineHeight: 1 }}>{risk ? risk.score : '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">of 100</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 150 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Risk grade</Typography>
                    <Typography sx={{ fontSize: 44, fontWeight: 800, color: gcolor, lineHeight: 1.1 }}>{risk?.grade || '—'}</Typography>
                    {risk?.delta != null && risk.delta !== 0 && (
                      <Typography variant="body2" sx={{ color: risk.delta > 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                        {risk.delta > 0 ? 'Up' : 'Down'} {Math.abs(risk.delta)} points since the previous scan
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">Open findings weighted by severity</Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 8 }}>
              <Grid container spacing={2} sx={{ height: '100%' }}>
                {sevCards.map((s) => (
                  <Grid key={s} size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ height: '100%', border: 1, borderColor: 'divider', borderTop: `3px solid ${SEV_COLOR[s]}`, borderRadius: 3, p: 2, bgcolor: 'background.paper',
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{s}</Typography>
                      {busy ? <Skeleton width={50} height={50} /> : (
                        <Typography sx={{ fontSize: 40, fontWeight: 800, fontFamily: 'var(--fuzzscan-ui-font-mono)', color: SEV_COLOR[s], lineHeight: 1 }}>{sev[s]}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">open</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* attack surface + trend */}
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card title="Attack surface" action={<Button size="small" onClick={() => navigate('/targets')} sx={{ color: GREEN, fontWeight: 700 }}>Add or edit items</Button>}>
                {Object.keys(assetCounts).length === 0 && <Typography variant="body2" color="text.secondary">{busy ? 'Loading…' : 'Nothing attached yet.'}</Typography>}
                <Stack direction="row" gap={1.2} flexWrap="wrap">
                  {Object.entries(assetCounts).map(([t, n]) => (
                    <Box key={t} sx={{ border: 1, borderColor: 'divider', borderRadius: 3, px: 2, py: 1.2, minWidth: 110 }}>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--fuzzscan-ui-font-mono)', lineHeight: 1.1 }}>{n}</Typography>
                      <Typography variant="caption" color="text.secondary">{TYPE_LABEL[t] || t}</Typography>
                    </Box>
                  ))}
                </Stack>
                {(data?.assets || []).slice(0, 6).map((a) => (
                  <Stack key={a.id} direction="row" alignItems="center" gap={1.2} sx={{ py: 0.8, borderTop: 1, borderColor: 'divider', '&:first-of-type': { mt: 0 } }}>
                    <Chip size="small" label={TYPE_LABEL[a.type] || a.type} sx={{ minWidth: 96, fontWeight: 700 }} />
                    <Typography noWrap sx={{ fontFamily: 'var(--fuzzscan-ui-font-mono)', fontSize: 13 }}>{a.value}</Typography>
                  </Stack>
                ))}
                {(data?.assets || []).length > 6 && <Typography variant="caption" color="text.secondary">+{data.assets.length - 6} more</Typography>}
              </Card>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Card title="Open findings, last 6 scans">
                {trend.length === 0 ? <Typography variant="body2" color="text.secondary">{busy ? 'Loading…' : 'Run a scan to see the trend.'}</Typography> : (
                  <>
                    <Typography variant="caption" color="text.secondary">Going down is good</Typography>
                    <Stack direction="row" alignItems="flex-end" gap={1.5} sx={{ height: 150, mt: 1 }}>
                      {trend.map((t, i) => (
                        <Stack key={t.scan} alignItems="center" justifyContent="flex-end" sx={{ flex: 1, height: '100%' }} gap={0.5}>
                          <Typography variant="caption" sx={{ fontFamily: 'var(--fuzzscan-ui-font-mono)' }}>{t.open}</Typography>
                          <Box sx={{ width: '100%', height: `${Math.max(4, (t.open / maxOpen) * 100)}px`, borderRadius: '6px 6px 0 0',
                            bgcolor: i === trend.length - 1 ? GREEN : 'action.selected' }} />
                          <Typography variant="caption" color="text.secondary">{when(t.date)}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}
              </Card>
            </Grid>
          </Grid>

          {/* fix first + side */}
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card title="Fix these first" action={<Button size="small" onClick={() => navigate('/vulns')} sx={{ color: GREEN, fontWeight: 700 }}>Open all findings</Button>}>
                {(data?.top || []).length === 0 && <Typography variant="body2" color="text.secondary">{busy ? 'Loading…' : 'No open findings.'}</Typography>}
                {(data?.top || []).map((f) => (
                  <Stack key={f.id} direction="row" alignItems="flex-start" gap={1.5} sx={{ py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Chip size="small" label={f.severity} sx={{ textTransform: 'capitalize', fontWeight: 700, minWidth: 64, bgcolor: alpha(SEV_COLOR[f.severity] || SEV_COLOR.info, 0.16), color: SEV_COLOR[f.severity] || SEV_COLOR.info }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }}>{f.title}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>{String(f.description || '').split('\n')[0]}</Typography>
                    </Box>
                    {f.tool && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'var(--fuzzscan-ui-font-mono)' }}>{f.tool}</Typography>}
                  </Stack>
                ))}
              </Card>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Stack spacing={2.5}>
                <Card title="Run something" action={<Button size="small" onClick={() => navigate('/jobs')} sx={{ color: GREEN, fontWeight: 700 }}>All jobs</Button>}>
                  <Grid container spacing={1}>
                    {runs.map(([label, path]) => (
                      <Grid key={label} size={6}>
                        <Button fullWidth variant="outlined" onClick={() => navigate(path)} sx={{ minHeight: 44, borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>{label}</Button>
                      </Grid>
                    ))}
                  </Grid>
                  {(data?.scans || []).slice(0, 3).map((s) => (
                    <Box key={s.id} sx={{ mt: 1.8 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{String(s.scan_type).replace(/_/g, ' ')}</Typography>
                        <Chip size="small" label={String(s.status).toUpperCase()} sx={{ fontWeight: 700, bgcolor: alpha(STATUS_COLOR[s.status] || '#94a3b8', 0.16), color: STATUS_COLOR[s.status] || '#94a3b8' }} />
                      </Stack>
                      <LinearProgress variant="determinate" value={Number(s.progress ?? (s.status === 'completed' ? 100 : 0))}
                        sx={{ mt: 0.7, height: 5, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: STATUS_COLOR[s.status] || '#94a3b8' } }} />
                    </Box>
                  ))}
                </Card>
                <Card title="Assessments" action={<Button size="small" onClick={() => navigate('/scan-diff')} sx={{ color: GREEN, fontWeight: 700 }}>Compare</Button>}>
                  {latestAudit && (
                    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                      <Typography variant="body2">Active Directory audit</Typography>
                      <Typography variant="body2" color="text.secondary">{when(latestAudit.created_at)}</Typography>
                    </Stack>
                  )}
                  {(data?.periods || []).length === 0 && !latestAudit && <Typography variant="body2" color="text.secondary">Import a scan to start tracking progress by month.</Typography>}
                  {(data?.periods || []).slice(0, 4).map((p) => (
                    <Stack key={p.id} direction="row" justifyContent="space-between" sx={{ py: 0.6 }}>
                      <Typography variant="body2">{p.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{p.findings} findings</Typography>
                    </Stack>
                  ))}
                </Card>
              </Stack>
            </Grid>
          </Grid>

          <Card title="Reports and downloads"
            action={
              <TextField select size="small" value={lang} onChange={(e) => setLang(e.target.value)} sx={{ minWidth: 110 }} aria-label="Report language">
                <MenuItem value="en">English</MenuItem><MenuItem value="fr">Français</MenuItem>
              </TextField>
            }>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Executive summary</Typography>
                <Button variant="outlined" onClick={() => navigate('/executive')}>Open and export PDF</Button>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  Active Directory audit {latestAudit ? `(${(latestAudit.domains || []).join(', ') || 'latest'})` : ''}
                </Typography>
                {latestAudit ? (
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {['pdf', 'docx', 'xlsx', 'csv'].map((f) => (
                      <Button key={f} size="small" variant="outlined" onClick={() => dl(() => downloadAdReport(latestAudit.id, f, lang))}>{f.toUpperCase()}</Button>
                    ))}
                  </Stack>
                ) : <Button variant="outlined" onClick={() => navigate('/ad-audit')}>Run an AD audit</Button>}
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Reconnaissance results</Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {['txt', 'json', 'csv', 'html'].map((f) => (
                    <Button key={f} size="small" variant="outlined" onClick={() => dl(() => downloadReconExport(currentTargetId, f))}>{f.toUpperCase()}</Button>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
